import {
  IsArray,
  IsBoolean,
  IsNotEmpty,
  IsNumber,
  IsObject,
  IsOptional,
  IsString
} from 'class-validator';
import { IProductCategory } from './product-category.dto';
import type { Types } from 'mongoose';
import type { IGiftCardRestrictions } from './gift-card';
import type { ISubscriptionExtras } from './subscription.dto';

export type ProductV2Status = 'active' | 'disabled';
export type ProductV2Type = 'physical' | 'digital';
export type SubscriptionCycleUnit = 'day' | 'week' | 'month' | 'year';
export type DelayedCaptureCustomerFilter = 'all' | 'first_order_only';
export type DelayedCaptureSubscriptionFilter = 'initial_only' | 'all_cycles';
export type DelayedCaptureValueType = 'dollar' | 'percent';

/**
 * Partial pay-later configuration. When present, only this portion of the item
 * total is deferred and the remainder is charged at checkout. When absent, the
 * full item total is deferred (legacy 100% behaviour).
 */
export interface IDelayedCaptureConfig {
  type: DelayedCaptureValueType;
  value: number; // dollar amount per unit or percent of item price
}

export interface IProductDelayedCapture {
  enabled: boolean;
  customerFilter: DelayedCaptureCustomerFilter;
  subscriptionFilter?: DelayedCaptureSubscriptionFilter;
  type?: DelayedCaptureValueType; // partial pay-later type; undefined = full item price
  value?: number; // partial pay-later value; undefined = full item price
  /** @deprecated Legacy per-unit dollar amount; use type/value after migration */
  amount?: number;
}
export class ProductV2Image {
  imageUrl: string;
  name?: string;
  isThumbnail?: boolean;
  sortOrder?: number;
  description?: string;
}

export interface IProductV2Extras {
  pharmacies: { [x: string]: string | Array<string> };
  drugName?: string;
  drugStrength?: string;
  drugQty?: string;
  /**
   * Pharmacy names to include standard injection kit, optionally can be used as an object with keys as pharmacy and values as MedID (Must be Stored in Drug Catalogue)
   * @example ['Curexa']
   * @example {'Curexa' : 'Some Random MedID'}
   */
  includeInjectionKit?: string[] | { [x: string]: string };
  refills?: number;
  daysSupply?: number | string;
  /**
   * Addons are used to add additional drugs to the order.
   * @example {'Curexa' : ['DIN001', 'DIN002']}
   * @example {'Curexa' : 'DIN001'}
   */
  addons?: { [pharmacyName: string]: string[] | string };
  /**
   * Weightloss Subscription :: Used to identify that the first phase of the subscription is going to query for drugs in the Drug Catalog with the flag isStarter.
   */
  isStarter?: boolean;
  Curexa?: {
    OTC?: {
      name: string;
      quantity: number;
    }[];
  };
  belugaMappedFields?: Record<string, any>;
  /**
   * When true, this line item is left out of the Beluga patient preference.
   * Use it for an item that shares an order with a configured medication but
   * has no MedId for the selected pharmacy.
   */
  skipBeluga?: boolean;
  /**
   * Physician-network key from PHYSICIAN_NETWORKS. Copied onto order line items.
   * `autoAssignPhysicianNetwork` matches this to PHYSICIAN_NETWORKS[].key.
   * @example 'beluga'
   * @example 'mdi'
   * @example 'arora'
   */
  physicianNetwork?: string;
  /**
   * MD Integrations offering id for this product.
   *
   * The MDI analogue of `pharmacies` above: Beluga resolves its external med id
   * off the line item as `extras.pharmacies[pharmacyKey]`, because a Beluga med
   * id is pharmacy-scoped. An MDI offering id is network-scoped — one id per
   * product, whichever pharmacy fills it — so it is a flat key rather than a map.
   *
   * Offerings are built by MDI's clinical team from the partner's spreadsheet and
   * referenced by id; MDI's default workflow does not allow open-catalog
   * prescribing. A product with no id here cannot be put on an MDI case, and
   * `BaseMdiCase.validate()` refuses the case rather than quietly dropping it.
   */
  mdiOfferingId?: string;
  /**
   * MDI disease (ICD) ids to attach to a case carrying this product, so it routes
   * to the right specialty.
   *
   * Worth setting rather than optional: every offering in the partner catalog
   * carries `specialty_id: null`, so there is nothing for MDI to infer from.
   */
  mdiDiseaseIds?: string[];
  /**
   * Deliberate overrides of the prescription MDI's clinical team already wrote
   * on the offering.
   *
   * An MDI offering arrives complete — quantity, days supply, dispense unit,
   * directions (including titration schedules), and the pharmacy that fills it,
   * with `force_pharmacy` set on almost all of them. Anything sent in the case's
   * `product` block replaces those values, so nothing here is defaulted or
   * derived from the order line: an unset key means "use MDI's own setup", and
   * `product` is omitted entirely when none are set.
   *
   * They are `mdi`-prefixed rather than reusing `drugName` / `drugQty` / `sig`,
   * which other pharmacy integrations already populate — reading those would
   * turn one vendor's configuration into a silent prescription override here.
   */
  mdiProductName?: string;
  mdiQuantity?: string | number;
  mdiDaysSupply?: string | number;
  mdiDirections?: string;
  mdiDispenseUnit?: string;
  mdiPharmacyNotes?: string;
  subscription?: ISubscriptionExtras;
  [key: string]: any;
}

export class IProductV2 {
  _id?: string;
  type: ProductV2Type;
  organization?: string;
  label: string;
  subLabel?: string;
  description?: string;
  listPrice?: number;
  price: number;
  cost?: number;
  visitFee?: number;
  shippingFee?: number;
  currency?: string;
  status: ProductV2Status;
  sku?: string;
  images?: ProductV2Image[];
  extras?: IProductV2Extras;
  isSubscription?: boolean;
  requirements?: IProductRequirement[];
  subscriptionPhases?: IProductSubscriptionPhase[];
  url?: string;
  options?: IProductV2Option[];
  discounts?: any[];
  isRequiredShipping?: boolean;
  weight?: number;
  width?: number;
  height?: number;
  depth?: number;
  relatedProducts?: RelatedProduct[];
  createdAt?: Date;
  updatedAt?: Date;
  categories?: (IProductCategory | Types.ObjectId)[];
  variants?: {
    id?: number;
    price: number;
    listPrice: number;
    sku: string;
  }[]; //for backward compatibility
  postPurchaseCredit?: number;
  vendor?: string;
  giftCardValue?: number;
  giftCardRestrictions?: IGiftCardRestrictions;
  delayedCapture?: IProductDelayedCapture;
  /** When true, excluded from JJC transfer-out eligible group; goes through normal checkout order path. */
  isCustomCompound?: boolean;
  /**
   * When true, this product requires no prescription. An order whose line items are
   * *all* non-prescription skips requirements, pharmacy assignment and
   * script/fulfillment and goes straight to `awaiting_shipment`; such products are
   * also split into their own order at checkout so they are never held behind a
   * prescription item.
   *
   * Absent is treated as `false` (prescription required), so existing products keep
   * their current routing.
   */
  isNonPrescription?: boolean;
  /** Subscription checkout skips saving the payment method so BNPL is offered; renewals are then paid manually. */
  allowBNPL?: boolean;
}

export class IProductV2Option {
  type: string;
  choices: {
    label: string;
    value: string;
    default: boolean;
    selected?: boolean;
  }[];
}

export class IProductRequirement {
  type: string;
  key: string;
}

export type RelatedProductType =
  | 'variant'
  | 'crossSell'
  | 'upsell'
  | 'contraindication';

export class RelatedProductImages {
  thumbnailUrl: string;
  description: string;
}

export class RelatedProduct {
  productId: string;
  relationType: RelatedProductType;
  displayName: string;
  images?: RelatedProductImages[]; // must use a class for swagger docs to work
  // to consider: maybe add the whole line item here?
  price: number;
  listPrice: number;
  variantId: string | number; // for backward compatibility and upsell
  extras?: IProductV2Extras;
  url?: string;
}

export class IProductSubscriptionPhase {
  fillingCycleInterval: number;
  fillingCycleUnit: SubscriptionCycleUnit;
  billableCycleInterval: number; // number of filling cycles before a payment is required (default is 1)
  fulfillmentCycleInterval?: number; // every Nth cycle is a fulfillment cycle (default is 1)
  requirementCycleInterval?: number; // every Nth cycle requires a new requirement; existing phase requirements are archived (default is 1)
  cycles?: number;
  price?: number;
  requirements?: IProductRequirement[];
}

export class CreateProductV2Dto {
  @IsNotEmpty()
  @IsString()
  label: string;

  @IsOptional()
  @IsString()
  subLabel?: string;

  @IsNotEmpty()
  @IsString()
  type: ProductV2Type;

  @IsNumber()
  listPrice: number;

  @IsNumber()
  price: number;

  @IsNumber()
  cost: number;

  @IsNumber()
  visitFee: number;

  @IsNumber()
  shippingFee: number;

  @IsOptional()
  @IsString()
  organization?: string;

  @IsOptional()
  @IsString()
  description?: string;

  @IsString()
  currency?: string;

  @IsOptional()
  @IsString()
  status: ProductV2Status;

  @IsOptional()
  @IsString()
  sku?: string;

  @IsOptional()
  @IsArray()
  images?: ProductV2Image[];

  @IsObject()
  @IsString()
  extras?: any;

  @IsBoolean()
  isSubscription?: boolean;

  @IsOptional()
  @IsArray()
  subscriptionPhases?: IProductSubscriptionPhase[];

  @IsOptional()
  @IsArray()
  requirements?: IProductRequirement[];

  @IsOptional()
  @IsString()
  url?: string;

  @IsOptional()
  @IsArray()
  options?: IProductV2Option[];

  @IsOptional()
  @IsArray()
  discounts?: any[];

  @IsOptional()
  @IsBoolean()
  isRequiredShipping?: boolean;

  @IsOptional()
  @IsNumber()
  weight?: number;

  @IsOptional()
  @IsNumber()
  width?: number;

  @IsOptional()
  @IsNumber()
  depth?: number;

  @IsOptional()
  @IsNumber()
  height?: number;

  @IsOptional()
  @IsArray()
  relatedProducts?: RelatedProduct[];

  @IsOptional()
  @IsArray()
  categories?: Types.ObjectId[];

  @IsOptional()
  @IsNumber()
  postPurchaseCredit?: number;

  @IsOptional()
  @IsString()
  vendor?: string;

  @IsOptional()
  @IsNumber()
  giftCardValue?: number;

  @IsOptional()
  @IsObject()
  giftCardRestrictions?: IGiftCardRestrictions;

  @IsOptional()
  @IsObject()
  delayedCapture?: IProductDelayedCapture;

  @IsOptional()
  @IsBoolean()
  isCustomCompound?: boolean;

  @IsOptional()
  @IsBoolean()
  isNonPrescription?: boolean;

  @IsOptional()
  @IsBoolean()
  allowBNPL?: boolean;
}

export type CreateProductV2Response = IProductV2;

export class UpdateProductV2Dto {
  @IsNotEmpty()
  @IsString()
  label: string;

  @IsOptional()
  @IsString()
  subLabel?: string;

  @IsNotEmpty()
  @IsString()
  type: ProductV2Type;

  @IsNumber()
  listPrice: number;

  @IsNumber()
  price: number;

  @IsNumber()
  cost: number;

  @IsNumber()
  visitFee: number;

  @IsNumber()
  shippingFee: number;

  @IsOptional()
  @IsString()
  organization?: string;

  @IsOptional()
  @IsString()
  description?: string;

  @IsOptional()
  @IsString()
  currency?: string;

  @IsOptional()
  @IsString()
  status: ProductV2Status;

  @IsOptional()
  @IsString()
  sku?: string;

  @IsOptional()
  @IsArray()
  images?: ProductV2Image[];

  @IsObject()
  @IsString()
  extras?: any;

  @IsOptional()
  @IsBoolean()
  isSubscription?: boolean;

  @IsOptional()
  @IsArray()
  subscriptionPhases?: IProductSubscriptionPhase[];

  @IsOptional()
  @IsArray()
  requirements?: IProductSubscriptionPhase[];

  @IsOptional()
  @IsString()
  url?: string;

  @IsOptional()
  @IsArray()
  options?: IProductV2Option[];

  @IsOptional()
  @IsArray()
  discounts?: any[];

  @IsOptional()
  @IsBoolean()
  isRequiredShipping?: boolean;

  @IsOptional()
  @IsNumber()
  weight?: number;

  @IsOptional()
  @IsNumber()
  width?: number;

  @IsOptional()
  @IsNumber()
  depth?: number;

  @IsOptional()
  @IsNumber()
  height?: number;

  @IsOptional()
  @IsArray()
  relatedProducts?: RelatedProduct[];

  @IsOptional()
  @IsArray()
  categories?: Types.ObjectId[];

  @IsOptional()
  @IsNumber()
  postPurchaseCredit?: number;

  @IsOptional()
  @IsString()
  vendor?: string;

  @IsOptional()
  @IsNumber()
  giftCardValue?: number;

  @IsOptional()
  @IsObject()
  giftCardRestrictions?: IGiftCardRestrictions;

  @IsOptional()
  @IsObject()
  delayedCapture?: IProductDelayedCapture;

  @IsOptional()
  @IsBoolean()
  isCustomCompound?: boolean;

  @IsOptional()
  @IsBoolean()
  isNonPrescription?: boolean;

  @IsOptional()
  @IsBoolean()
  allowBNPL?: boolean;
}

export type UpdateProductV2Response = IProductV2;

export class GetProductV2Dto {
  status?: ProductV2Status;
  label?: string;
  _id?: string;
  organization?: string;
  price?: string;
  type?: string;
  isSubscription?: string;
  categories?: Types.ObjectId[];
  sortField?: string;
  sortOrder?: number;
}

export type GetProductV2Response = IProductV2[];
